import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, Gift, Loader2, Minus, Plus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { KuponDialog, PromoInput } from "@/components/akaun/Promo";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/lib/me";

export const rm = (sen) => `RM${(sen / 100).toFixed(2)}`;
export const tarikh = (iso) => (iso ? new Date(iso).toLocaleDateString("ms-MY", { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS = { paid: "Dibayar", pending: "Menunggu", gagal: "Gagal", gagal_cipta: "Gagal", diganti: "Diproses manual" };

const Stat = ({ label, value, testid, sub }) => (
  <div className="rounded-xl border border-line p-4" data-testid={testid}>
    <div className="eyebrow">{label}</div>
    <div className="font-display text-2xl text-ink mt-1">{value}</div>
    {sub && <div className="text-xs text-mute mt-1">{sub}</div>}
  </div>
);

const Balance = ({ a, unlimited, harga, onPay, busy }) => (
  <section className="surface rounded-2xl p-6 sm:p-8 space-y-6" data-testid="akaun-balance">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="eyebrow">Baki carian</div>
        <div className="font-display text-5xl text-q mt-2" data-testid="akaun-total">{unlimited ? "∞" : a.jumlah_baki}</div>
        <div className="text-sm text-mute mt-1">Hanya penjanaan AI dikira: "Jana HTE" dan "Bina Kerangka Program". Laras lapis, carian calon ayat, sunting, salin dan AAR adalah percuma.</div>
      </div>
      <div className="sm:text-right">
        <div className="eyebrow">Pelan</div>
        <div className="font-display text-xl text-ink" data-testid="akaun-plan">{a.plan_nama}</div>
        {a.plan_expires_at && <div className="text-xs text-mute">Tamat {tarikh(a.plan_expires_at)} ({a.hari_lagi} hari lagi)</div>}
        {a.plan !== "percuma" && (
          <button onClick={() => onPay(a.plan, 1)} disabled={busy} data-testid="akaun-renew-button"
            className="mt-3 h-10 px-5 rounded-full bg-q text-bg text-sm font-semibold inline-flex items-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-40">
            <RotateCw size={14} /> Perbaharui 30 hari lagi · {rm(harga)}
          </button>
        )}
      </div>
    </div>
    {a.peringatan && (
      <div className="rounded-xl p-3 bg-warn-soft text-warn text-sm flex gap-2" data-testid="akaun-renew-reminder">
        <AlertTriangle size={16} className="shrink-0 mt-0.5" /> Langganan anda tamat dalam {a.hari_lagi} hari. Bayar semula untuk menyambung 30 hari lagi.
      </div>
    )}
    <div className="rounded-xl border border-line p-4 grid sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3 text-center" data-testid="akaun-breakdown">
      <div><div className="eyebrow">Baki percuma</div><div className="font-display text-2xl text-ink mt-1" data-testid="akaun-baki-percuma">{a.baki_percuma}</div><div className="text-xs text-mute">{a.plan === "percuma" ? "Termasuk kuota percuma bulan ini" : "Disimpan semasa melanggan, tidak luput"}</div></div>
      <Plus size={16} className="text-mute mx-auto" />
      <div><div className="eyebrow">Kuota pelan</div><div className="font-display text-2xl text-ink mt-1" data-testid="akaun-kuota-pelan">{a.plan === "percuma" ? "—" : `${a.baki_pelan}`}</div><div className="text-xs text-mute">{a.plan === "percuma" ? "Tiada langganan aktif" : `Baki daripada ${a.plan_nama}`}</div></div>
      <span className="text-mute font-display text-xl">=</span>
      <div><div className="eyebrow">Jumlah keseluruhan</div><div className="font-display text-2xl text-q mt-1" data-testid="akaun-jumlah-keseluruhan">{a.baki_percuma + a.baki_pelan + a.baki_lain}</div><div className="text-xs text-mute">{a.baki_lain > 0 ? `Termasuk ${a.baki_lain} lain-lain (bawa ke hadapan, tambahan, Per Use)` : "Semua carian yang boleh digunakan"}</div></div>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Stat label={`Bulanan (${a.period})`} value={a.baki_bulanan} testid="akaun-monthly" sub="Reset pada 1 haribulan" />
      <Stat label="Bawa ke hadapan" value={a.carry} testid="akaun-carry" sub={`Maksimum ${a.had_carry}`} />
      <Stat label="Kuota tambahan" value={a.bonus} testid="akaun-bonus" sub="Daripada admin" />
      <Stat label="Pay-as-you-go" value={a.payg} testid="akaun-payg" sub="Tidak luput" />
    </div>
    {a.bonus_items.filter((b) => b.baki > 0).length > 0 && (
      <ul className="text-xs text-mute space-y-1" data-testid="akaun-bonus-items">
        {a.bonus_items.filter((b) => b.baki > 0).map((b) => (
          <li key={b.id} className="flex items-center gap-2"><Gift size={12} className="text-q" /> {b.baki}/{b.jumlah} {b.nota && `· ${b.nota}`} · {b.tamat ? `tamat ${tarikh(b.tamat)}` : "tiada tarikh tamat"}</li>
        ))}
      </ul>
    )}
  </section>
);

const Buy = ({ data, onPay, busy }) => {
  const [qty, setQty] = useState(5);
  const [promo, setPromo] = useState(null);
  const { pelan, payg_sen, akaun } = data;
  const card = "surface rounded-2xl p-6 flex flex-col gap-4";
  const btn = "mt-auto h-11 rounded-full bg-ink text-bg text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2";
  return (
    <section className="space-y-4">
      <div className="eyebrow">Tambah carian</div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className={card} data-testid="buy-payg">
          <div className="font-display text-xl">Pay-as-you-go</div>
          <div className="text-sm text-mute">{rm(payg_sen)} setiap carian tambahan. Tidak luput.</div>
          <div className="flex items-center gap-3">
            <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Kurang" data-testid="payg-minus" className="h-9 w-9 rounded-full border border-line grid place-items-center"><Minus size={14} /></button>
            <input value={qty} onChange={(e) => setQty(Math.min(200, Math.max(1, Number(e.target.value) || 1)))} data-testid="payg-qty" className="w-16 h-9 text-center rounded-lg bg-bg border border-line font-mono-data" />
            <button onClick={() => setQty(Math.min(200, qty + 1))} aria-label="Tambah" data-testid="payg-plus" className="h-9 w-9 rounded-full border border-line grid place-items-center"><Plus size={14} /></button>
          </div>
          <button onClick={() => onPay("payg", qty)} disabled={busy} className={btn} data-testid="buy-payg-button">Bayar {rm(payg_sen * qty)}</button>
        </div>
        {["asas50", "tanpahad"].map((id) => (
          <div key={id} className={`${card} ${akaun.plan === id ? "!border-[var(--q)]" : ""}`} data-testid={`buy-${id}`}>
            <div className="font-display text-xl">{pelan[id].nama}</div>
            <div className="text-sm text-mute">{rm(pelan[id].harga_sen)} untuk 30 hari · {id === "tanpahad" ? "carian tanpa had (fair use 300 sebulan)" : "50 carian sebulan"}. Baki tidak digunakan dibawa ke hadapan (maks. {pelan[id].kuota * 5}).</div>
            {akaun.plan === id && <div className="text-xs text-q inline-flex items-center gap-1"><Check size={12} /> Pelan semasa · bayar lagi untuk lanjutkan 30 hari</div>}
            {id === "tanpahad" && <PromoInput produk={id} promo={promo} setPromo={setPromo} />}
            <button onClick={() => onPay(id, 1, id === "tanpahad" ? promo?.kod : "")} disabled={busy} className={btn} data-testid={`buy-${id}-button`}>
              {akaun.plan === id ? "Sambung" : "Langgan"} {id === "tanpahad" && promo ? <><s className="opacity-60">{rm(pelan[id].harga_sen)}</s> {rm(promo.harga_sen)}</> : rm(pelan[id].harga_sen)}
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-mute">Pembayaran melalui OnPay (FPX / kad). Langganan ialah bayaran sekali untuk 30 hari, bukan caj automatik. <strong data-testid="akaun-email-notice">Penting: masukkan emel yang sama dengan akaun ATAS anda di borang OnPay supaya kuota dikemas kini secara automatik.</strong>{data.mode === "mock" && " Mod ujian dalaman aktif: tiada wang sebenar ditolak."}</p>
    </section>
  );
};

const History = ({ items, onPay, busy }) => (
  <section className="space-y-3">
    <div className="eyebrow">Sejarah bayaran</div>
    {items.length === 0 ? <p className="text-sm text-mute">Belum ada bayaran.</p> : (
      <ul className="space-y-2" data-testid="akaun-payments">
        {items.map((p) => (
          <li key={p.id} className="surface rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`payment-row-${p.id}`}>
            <span className="font-mono-data text-xs text-mute">{tarikh(p.created_at)}</span>
            <span className="flex-1">{p.produk === "payg" ? `${p.qty} carian tambahan` : p.produk === "asas50" ? "Langganan 50 carian" : "Langganan tanpa had"}</span>
            <span className="font-mono-data">{rm(p.amount_sen)}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-i-soft text-i" : "bg-warn-soft text-warn"}`}>{STATUS[p.status] || p.status}</span>
            {p.status === "paid" && (
              <button onClick={() => onPay(p.produk, p.qty)} disabled={busy} data-testid={`payment-repay-${p.id}`}
                className="h-8 px-3 rounded-full border border-line text-xs inline-flex items-center gap-1 transition-colors hover:border-[var(--q)] hover:text-q disabled:opacity-40">
                <RotateCw size={12} /> Bayar lagi
              </button>
            )}
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default function AkaunPage() {
  const api = useApi();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kupon, setKupon] = useState(null);
  const load = useCallback(() => api("get", "/billing/me").then(setData).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const handled = useRef(false);
  useEffect(() => {
    const pid = params.get("bayaran") || localStorage.getItem("atas-bayaran");
    if (!pid || handled.current) return;
    handled.current = true;
    api("get", `/billing/payments/${pid}`).then((p) => {
      if (p.status === "paid") toast.success("Bayaran berjaya. Baki carian dikemas kini.");
      else if (p.status === "pending") toast("Bayaran masih diproses. Muat semula sebentar lagi.");
      else toast.error("Bayaran tidak berjaya.");
      if (p.status !== "pending") localStorage.removeItem("atas-bayaran");
      setParams({}, { replace: true }); load(); me?.refresh();
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = params.get("perbaharui");
    if (!data || !id) return;
    document.querySelector(`[data-testid="buy-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (produk, qty, kod = "") => {
    setBusy(true);
    try {
      const r = await api("post", "/billing/checkout", { produk, qty, kod: kod || "" });
      localStorage.setItem("atas-bayaran", r.payment_id);
      if (r.kupon && data?.mode !== "mock") { setKupon(r); setBusy(false); return; }
      window.location.assign(r.url);
    }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14 space-y-8">
      <header>
        <div className="eyebrow">Akaun</div>
        <h1 className="font-display text-4xl sm:text-5xl text-ink mt-3">Kuota & langganan</h1>
        {me?.role === "superadmin" && <Link to="/admin/bil" className="sm:hidden inline-flex mt-4 h-10 px-4 items-center rounded-full border border-line text-sm" data-testid="akaun-admin-link">Panel admin</Link>}
      </header>
      {!data ? <Loader2 className="animate-spin text-q" /> : <>
        <Balance a={data.akaun} unlimited={data.superadmin} harga={data.pelan[data.akaun.plan]?.harga_sen || 0} onPay={pay} busy={busy} />
        <Buy data={data} onPay={pay} busy={busy} />
        <History items={data.bayaran} onPay={pay} busy={busy} />
      </>}
      {kupon && <KuponDialog info={kupon} onClose={() => setKupon(null)} />}
    </div>
  );
}
